import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { MOUSE_CONSTRAINT_DISTANCE } from "@miplatform/constants";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Box, Grid } from "@mui/material";
import { Bucket } from "./Bucket/Bucket";
import { Card } from "./Card/Card";
import { Column } from "./Column/Column";
import { ColumnHeader } from "./ColumnHeader/ColumnHeader";
import { useMiKanbanLegacyViewModel } from "./useMiKanbanLegacyViewModel";

const useStyles = makeStyles()((theme) => ({
  container: {
    flex: 1,
    position: "relative",
  },
  columnsContainer: {
    height: "100%",
    flexWrap: "nowrap",
    display: "flex",
    gap: 8,
    "&::-webkit-scrollbar": { display: "none" } /* webkit */,
    msOverflowStyle: "none" /* IE and Edge */,
    scrollbarWidth: "none" /* Firefox */,
  },
  columnHeaders: {
    display: "flex",
    gap: 8,
    "&::-webkit-scrollbar": { display: "none" },
    msOverflowStyle: "none" /* IE and Edge */,
    scrollbarWidth: "none" /* Firefox */,
  },
  stickyTopArea: {
    backgroundColor: theme.palette.background.default,
    position: "sticky",
    top: 0,
    zIndex: 99,
  },
  boardWrapper: {
    flex: 1,
    overflow: "auto",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
}));

export function MiKanbanLegacy({
  data = [],
  columns: kanbanColumns = [],
  columnKeyField,
  buckets: kanbanBuckets = [],
  CustomCard = () => null,
  CustomColumnHeader = () => null,
  onColumnHeaderClick = () => null,
}) {
  const {
    columns,
    buckets,
    activeCard,
    prepareBuckets,
    prepareColumns,
    handleDragEnd,
    handleDragOver,
    handleDragStart,
  } = useMiKanbanLegacyViewModel({
    columnKeyField,
    kanbanColumns,
  });
  const { classes } = useStyles();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: MOUSE_CONSTRAINT_DISTANCE,
      },
    })
  );

  useEffect(() => {
    if (data) {
      prepareColumns(data);
    }
  }, [data, kanbanColumns]);

  useEffect(() => {
    if (kanbanBuckets) {
      prepareBuckets(kanbanBuckets);
    }
  }, [kanbanBuckets]);

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
    >
      <Box className={classes.boardWrapper}>
        <Box className={`${classes.stickyTopArea} ${classes.columnHeaders}`}>
          {kanbanColumns.map((col) => (
            <ColumnHeader
              key={col.recievedDate}
              onClick={() => {
                onColumnHeaderClick(col);
              }}
            >
              <CustomColumnHeader key={col.recievedDate} column={col} />
            </ColumnHeader>
          ))}
        </Box>

        <Box className={classes.container} data-testid="day-view">
          <Grid className={classes.columnsContainer} container>
            {columns.map(([key, cards]) => (
              <Column id={key} key={key} cards={cards}>
                {cards.map((card) => (
                  <Card key={card.uuid} card={card}>
                    <CustomCard card={card} />
                  </Card>
                ))}
              </Column>
            ))}
          </Grid>
        </Box>
      </Box>
      {buckets.map((bucket) => (
        <Bucket key={bucket.id} bucket={bucket}>
          {(bucket?.data || []).map((card) => (
            <Card key={card.uuid} card={card}>
              <CustomCard card={card} />
            </Card>
          ))}
        </Bucket>
      ))}
      <DragOverlay>
        {activeCard ? (
          <Card card={activeCard}>
            <CustomCard card={activeCard} />
          </Card>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
